import React, { useState } from 'react';
import {
	Card,
	CardContent,
	Button,
	SearchBar,
	SimpleTabBar,
	Alert,
	AlertTitle,
	useToast,
	PageHeader,
	TableSkeleton,
	EmptyState
} from '../ui';
import { ClientsTable, MetricTiles, AddClientForm, AddAccountForm } from '../composite';
import { PersonIcon } from '@radix-ui/react-icons';
import { useClientList } from '../features/clients/useClientList';
import { DeletionStatusPanel } from '../features/clients/DeletionStatusPanel';

/**
 * ClientList - Redesigned page component using new architecture
 * 
 * Demonstrates separation of concerns:
 * - Business logic in useClientList hook
 * - UI rendering with design primitives
 * - Clean composition of components
 */
export const ClientListPage = () => {
	const toast = useToast();
	const [selectedClient, setSelectedClient] = useState({ id: null, name: '' });
	const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);

	const {
		clients,
		loading,
		error,
		stats,
		searchTerm,
		selectedPlatform,
		sortBy,
		activeTab,
		hasFilters,
		isEmpty,
		isFiltered,
		updateSearch,
		updateSorting,
		updateTab,
		updatePlatformFilter,
		resetFilters,
		refresh,
	} = useClientList();

	// Tab configuration
	const tabs = [
		{ id: 'all', label: 'All clients', count: stats.totalClients },
		{ id: 'attention', label: 'Needs attention', count: stats.attentionClients },
		{ id: 'unscanned', label: 'Never scanned', count: stats.neverScanned },
	];

	// Sort options
	const sortOptions = [
		{ value: 'lastScan', label: 'Last Scan' },
		{ value: 'name', label: 'Name' },
		{ value: 'risk', label: 'Risk Score' },
		{ value: 'category', label: 'Category' },
	];

	const handleAddAccount = (clientId, clientName) => {
		setSelectedClient({ id: clientId, name: clientName });
		setIsAddAccountModalOpen(true);
	};

	const handleClientCreated = (clientData) => {
		toast.success(`${clientData?.name || 'Client'} added`);
	};

	const handleAccountCreated = (accountData) => {
		toast.success(`@${accountData?.handle || 'account'} is now monitored`);
		setIsAddAccountModalOpen(false);
		setSelectedClient({ id: null, name: '' });
	};

	return (
		<div className="min-h-screen bg-muted">
			<div className="max-w-7xl mx-auto p-6">
				{/* Header */}
				<PageHeader
					title="Clients"
					description="Manage your clients and their social media accounts"
					actions={<>
						<Button variant="outline" onClick={refresh}>Refresh</Button>
						<AddClientForm
							trigger={<Button>Add client</Button>}
							onSuccess={handleClientCreated}
						/>
					</>}
				/>
				<DeletionStatusPanel />

				{/* Stats Section */}
				<div className="mb-5">
						<MetricTiles
							items={[
								{ label: 'Clients', value: stats.totalClients, icon: 'clients', accent: 'brand' },
								{ label: 'Needs attention', value: stats.attentionClients, icon: 'risk', accent: 'amber' },
								{ label: 'Never scanned', value: stats.neverScanned, icon: 'active', accent: 'green' },
							]}
						/>
					</div>

				{/* Tabs */}
				<div className="mb-4">
					<SimpleTabBar
						tabs={tabs}
						activeTab={activeTab}
						onTabChange={updateTab}
						showCounts
						variant="underline"
					/>
				</div>

				{/* Search and Filters */}
				<div className="mb-4">
					<div>
						<SearchBar
							searchTerm={searchTerm}
							onSearchChange={updateSearch}
							selectedPlatform={selectedPlatform}
							onPlatformFilter={updatePlatformFilter}
							sortBy={sortBy}
							onSort={updateSorting}
							sortOptions={sortOptions}
							searchPlaceholder="Search clients by name, email, or category..."
						/>

						{hasFilters && (
							<div className="mt-4 flex items-center gap-2">
								<span className="text-sm text-muted-foreground">Active filters:</span>
								<Button variant="outline" size="sm" onClick={resetFilters}>
									Clear all filters
								</Button>
							</div>
						)}
					</div>
				</div>

				{/* Error State */}
				{error && (
					<Alert variant="destructive" className="mb-6">
						<AlertTitle>Error loading clients</AlertTitle>
						<p>{error}</p>
					</Alert>
				)}

				{/* Loading State */}
				{loading && <TableSkeleton rows={5} />}

				{/* Empty State */}
				{!loading && isEmpty && (
					<Card>
						<CardContent>
							<EmptyState
								icon={PersonIcon}
								title={isFiltered ? 'No clients match your filters' : 'No clients yet'}
								description={isFiltered
									? 'Try a different search, platform or tab.'
									: 'Add your first client to start monitoring their accounts.'}
								action={isFiltered ? (
									<Button variant="outline" onClick={resetFilters}>Clear filters</Button>
								) : (
									<AddClientForm
										trigger={<Button>Add your first client</Button>}
										onSuccess={handleClientCreated}
									/>
								)}
							/>
						</CardContent>
					</Card>
				)}

				{/* Clients Table */}
				{!loading && !isEmpty && (
					<ClientsTable
						clients={clients}
						onAddAccount={handleAddAccount}
						loading={loading}
					/>
				)}

				{/* Add Account Modal */}
				<AddAccountForm
					isOpen={isAddAccountModalOpen}
					onOpenChange={setIsAddAccountModalOpen}
					clientId={selectedClient.id}
					clientName={selectedClient.name}
					onSuccess={handleAccountCreated}
				/>
			</div>
		</div>
	);
};
